import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  X,
  Package,
  Truck,
  ShoppingCart,
  Tag,
  Check,
  Loader2,
  Calendar,
  Bell,
  RefreshCw,
  Settings,
  CheckCircle2,
  Info,
  AlertTriangle,
  Mail,
  ImageOff,
  FolderTree,
  TrendingUp,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuth } from '@/context/AuthContext';

interface PttAVMIntegration {
  id: string;
  name: string;
  nameEn: string;
  description: string;
  descriptionEn: string;
  tooltip: string;
  tooltipEn: string;
  icon: typeof Package;
  enabled: boolean;
  status: 'active' | 'inactive' | 'error' | 'rate_limited';
  hasRateLimit?: boolean;
  rateLimitNote?: string;
  rateLimitNoteEn?: string;
}

interface PttAVMSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  connectionId: string;
  onSave: (settings: PttAVMSettings) => void;
}

export interface PttAVMSettings {
  syncFrequency: number;
  notifications: boolean;
  integrations: Record<string, boolean>;
}

const defaultIntegrations: PttAVMIntegration[] = [
  {
    id: 'products',
    name: 'Urun Listeleme',
    nameEn: 'Product Listings',
    description: 'Urunlerinizi, stok ve fiyat bilgilerini goruntuluyin',
    descriptionEn: 'View your products, stock and price information',
    tooltip: 'Tum urunlerinizi, stok durumlarini, fiyatlarini ve listeleme durumlarini takip edin.',
    tooltipEn: 'Track all your products, stock levels, prices and listing status.',
    icon: Package,
    enabled: true,
    status: 'active',
    hasRateLimit: true,
    rateLimitNote: 'Bu endpoint 10 dakikada 1 kez sorgulanabilir.',
    rateLimitNoteEn: 'This endpoint can be queried once every 10 minutes.',
  },
  {
    id: 'orders',
    name: 'Siparis Entegrasyonu',
    nameEn: 'Order Integration',
    description: 'Siparisleri otomatik olarak cek ve analiz et',
    descriptionEn: 'Automatically fetch and analyze orders',
    tooltip: 'Tum siparis statuslerini takip edin. Siparis trendlerini ve en cok satan urunleri analiz edin.',
    tooltipEn: 'Track all order statuses. Analyze order trends and best-selling products.',
    icon: ShoppingCart,
    enabled: true,
    status: 'active',
    hasRateLimit: true,
    rateLimitNote: 'Bu endpoint 5 dakikada 1 kez sorgulanabilir. Tarih araligi max 40 gun.',
    rateLimitNoteEn: 'This endpoint can be queried once every 5 minutes. Date range max 40 days.',
  },
  {
    id: 'categories',
    name: 'Kategori Bilgileri',
    nameEn: 'Category Information',
    description: 'PttAVM kategori agacini ve bilgilerini goruntuluyin',
    descriptionEn: 'View PttAVM category tree and information',
    tooltip: 'Tum ana kategorileri goruntuluyin. Urunlerinizi dogru kategorilere yerlestirin.',
    tooltipEn: 'View all main categories. Place your products in the correct categories.',
    icon: Tag,
    enabled: true,
    status: 'active',
    hasRateLimit: true,
    rateLimitNote: 'Bu endpoint 30 dakikada 1 kez sorgulanabilir.',
    rateLimitNoteEn: 'This endpoint can be queried once every 30 minutes.',
  },
  {
    id: 'shipping',
    name: 'Kargo Analitigi',
    nameEn: 'Shipping Analytics',
    description: 'Teslimat performansi ve iade oranlarini analiz edin',
    descriptionEn: 'Analyze delivery performance and return rates',
    tooltip: 'Teslimat basari orani, iade orani, kargo durumlari ve lojistik performansi takip edin.',
    tooltipEn: 'Track delivery success rate, return rate, shipping statuses and logistics performance.',
    icon: Truck,
    enabled: true,
    status: 'active',
    hasRateLimit: true,
    rateLimitNote: 'Bu endpoint 5 dakikada 1 kez sorgulanabilir.',
    rateLimitNoteEn: 'This endpoint can be queried once every 5 minutes.',
  },
  {
    id: 'categoryTree',
    name: 'Kategori Agaci',
    nameEn: 'Category Tree',
    description: 'Tam kategori hiyerarsisini ve alt kategorileri goruntuluyin',
    descriptionEn: 'View full category hierarchy and subcategories',
    tooltip: 'Tum kategori agacini detayli goruntuluyin. Urun kategorilendirme optimizasyonu icin kullanin.',
    tooltipEn: 'View detailed category tree. Use for product categorization optimization.',
    icon: FolderTree,
    enabled: true,
    status: 'active',
    hasRateLimit: true,
    rateLimitNote: 'Bu endpoint 30 dakikada 1 kez sorgulanabilir.',
    rateLimitNoteEn: 'This endpoint can be queried once every 30 minutes.',
  },
  {
    id: 'imageErrors',
    name: 'Gorsel Hatalari',
    nameEn: 'Image Errors',
    description: 'Hatali gorsellerle urunleri tespit edin ve listing sagligini olcun',
    descriptionEn: 'Detect products with image errors and measure listing health',
    tooltip: 'Gorsel hatalari olan urunleri bulun. Listing health score hesaplayin. AI onerileri alin.',
    tooltipEn: 'Find products with image errors. Calculate listing health score. Get AI recommendations.',
    icon: ImageOff,
    enabled: true,
    status: 'active',
    hasRateLimit: true,
    rateLimitNote: 'Bu endpoint 15 dakikada 1 kez sorgulanabilir.',
    rateLimitNoteEn: 'This endpoint can be queried once every 15 minutes.',
  },
  {
    id: 'stockUpdate',
    name: 'Stok/Fiyat Guncelleme',
    nameEn: 'Stock/Price Update',
    description: 'AI onerilerine gore otomatik stok ve fiyat guncellemeleri',
    descriptionEn: 'Automatic stock and price updates based on AI recommendations',
    tooltip: 'AI tarafindan tespit edilen sorunlari otomatik duzeltme. Toplu fiyat/stok guncellemeleri.',
    tooltipEn: 'Auto-fix issues detected by AI. Bulk price/stock updates.',
    icon: TrendingUp,
    enabled: false,
    status: 'inactive',
    hasRateLimit: true,
    rateLimitNote: 'Bu endpoint 5 dakikada 1 kez kullanilabilir. Max 1000 urun/istek.',
    rateLimitNoteEn: 'This endpoint can be used once every 5 minutes. Max 1000 products/request.',
  },
];

const syncFrequencyOptions = [
  { value: 900, label: '15 Dakika', labelEn: '15 Minutes' },
  { value: 1800, label: '30 Dakika', labelEn: '30 Minutes' },
  { value: 3600, label: 'Saatlik', labelEn: 'Hourly' },
  { value: 21600, label: '6 Saatte Bir', labelEn: 'Every 6 Hours' },
  { value: 43200, label: '12 Saatte Bir', labelEn: 'Every 12 Hours' },
  { value: 86400, label: 'Gunluk', labelEn: 'Daily' },
];

export function PttAVMSettingsModal({
  isOpen,
  onClose,
  connectionId,
  onSave,
}: PttAVMSettingsModalProps) {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  const { user } = useAuth();

  const [integrations, setIntegrations] = useState<PttAVMIntegration[]>(defaultIntegrations);
  const [syncFrequency, setSyncFrequency] = useState(3600);
  const [notifications, setNotifications] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState<'integrations' | 'sync' | 'advanced'>('integrations');

  // Load saved settings on mount
  useEffect(() => {
    if (isOpen && user?.uid && connectionId) {
      loadSettings();
    }
  }, [isOpen, user?.uid, connectionId]);

  const loadSettings = async () => {
    try {
      const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';
      const response = await fetch(
        `${FUNCTIONS_URL}/getPttavmSettings?userId=${user?.uid}&connectionId=${connectionId}`
      );

      if (response.ok) {
        const data = await response.json();
        if (data.settings) {
          setSyncFrequency(data.settings.syncFrequency || 3600);
          setNotifications(data.settings.notifications ?? true);

          if (data.settings.integrations) {
            setIntegrations(prev => prev.map(int => ({
              ...int,
              enabled: data.settings.integrations[int.id] ?? true,
              status: data.settings.integrations[int.id] ? 'active' : 'inactive',
            })));
          }
        }
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const toggleIntegration = (id: string) => {
    setIntegrations(prev =>
      prev.map(int =>
        int.id === id
          ? { ...int, enabled: !int.enabled, status: !int.enabled ? 'active' : 'inactive' }
          : int
      )
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const settings: PttAVMSettings = {
        syncFrequency,
        notifications,
        integrations: integrations.reduce((acc, int) => {
          acc[int.id] = int.enabled;
          return acc;
        }, {} as Record<string, boolean>),
      };

      const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';
      await fetch(`${FUNCTIONS_URL}/savePttavmSettings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user?.uid,
          connectionId,
          settings,
        }),
      });

      onSave(settings);
      onClose();
    } catch (error) {
      console.error('Failed to save settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const getEndpointForIntegration = (id: string): string => {
    switch (id) {
      case 'products':
        return 'getPttavmListings';
      case 'orders':
        return 'getPttavmOrders';
      case 'categories':
        return 'getPttavmCategories';
      case 'shipping':
        return 'getPttavmShippingInfo';
      case 'categoryTree':
        return 'getPttavmCategoryTree';
      case 'imageErrors':
        return 'getPttavmImageErrors';
      case 'stockUpdate':
        return 'updatePttavmStock';
      default:
        return '';
    }
  };

  const handleSync = async (integrationId: string) => {
    if (!user?.uid || !connectionId) return;

    setIsSyncing(true);
    const integration = integrations.find(i => i.id === integrationId);
    if (!integration) return;

    try {
      const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';
      const endpoint = getEndpointForIntegration(integrationId);

      const response = await fetch(
        `${FUNCTIONS_URL}/${endpoint}?userId=${user.uid}&connectionId=${connectionId}&forceRefresh=true`
      );

      const data = await response.json();

      if (data._rateLimited) {
        setIntegrations(prev =>
          prev.map(int =>
            int.id === integrationId
              ? { ...int, status: 'rate_limited' }
              : int
          )
        );
      } else {
        setIntegrations(prev =>
          prev.map(int =>
            int.id === integrationId
              ? { ...int, status: 'active' }
              : int
          )
        );
      }
    } catch (error) {
      console.error('Sync failed:', error);
      setIntegrations(prev =>
        prev.map(int =>
          int.id === integrationId
            ? { ...int, status: 'error' }
            : int
        )
      );
    } finally {
      setIsSyncing(false);
    }
  };

  if (!isOpen) return null;

  const modal = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-bg-surface rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden border border-border-default flex flex-col">
        {/* Header */}
        <div
          className="relative p-6 pb-4 flex-shrink-0"
          style={{
            background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
          }}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-gray-800 transition-colors"
          >
            <X size={18} />
          </button>

          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center shadow-lg">
              <Mail size={28} className="text-[#FFD700]" />
            </div>
            <div className="text-gray-800">
              <h2 className="text-xl font-bold">PttAVM {isEnglish ? 'Settings' : 'Ayarlari'}</h2>
              <p className="text-gray-700 text-sm">
                {isEnglish ? 'Configure your integration' : 'Entegrasyonunuzu yapilandirin'}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border-default bg-bg-elevated flex-shrink-0">
          {[
            { id: 'integrations', label: isEnglish ? 'Integrations' : 'Entegrasyonlar', icon: Package },
            { id: 'sync', label: isEnglish ? 'Sync Settings' : 'Senkronizasyon', icon: RefreshCw },
            { id: 'advanced', label: isEnglish ? 'Advanced' : 'Gelismis', icon: Settings },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id as typeof activeTab)}
              className={cn(
                'flex-1 py-3 px-4 text-sm font-medium flex items-center justify-center gap-2 border-b-2 transition-colors',
                activeTab === id
                  ? 'border-[#FFD700] text-[#B8860B] bg-[#FFD700]/5'
                  : 'border-transparent text-text-secondary hover:text-text-primary hover:bg-bg-surface'
              )}
            >
              <Icon size={16} />
              {label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'integrations' && (
            <div className="space-y-4">
              <p className="text-sm text-text-secondary mb-4">
                {isEnglish
                  ? 'Enable or disable data sync for each integration.'
                  : 'Her entegrasyon icin veri senkronizasyonunu etkinlestirin veya devre disi birakin.'}
              </p>

              {integrations.map((integration) => (
                <div
                  key={integration.id}
                  className={cn(
                    'p-4 rounded-xl border transition-all',
                    integration.enabled
                      ? 'bg-[#FFD700]/5 border-[#FFD700]/30'
                      : 'bg-bg-elevated border-border-default'
                  )}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={cn(
                        'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
                        integration.enabled ? 'bg-[#FFD700]/20 text-[#B8860B]' : 'bg-bg-surface text-text-tertiary'
                      )}
                    >
                      <integration.icon size={20} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-text-primary">
                          {isEnglish ? integration.nameEn : integration.name}
                        </h4>
                        {integration.status === 'active' && integration.enabled && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/10 text-green-500">
                            {isEnglish ? 'Active' : 'Aktif'}
                          </span>
                        )}
                        {integration.status === 'rate_limited' && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/10 text-yellow-600">
                            {isEnglish ? 'Rate Limited' : 'Limit Asildi'}
                          </span>
                        )}
                        {integration.status === 'error' && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/10 text-red-500">
                            {isEnglish ? 'Error' : 'Hata'}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-text-secondary mt-1">
                        {isEnglish ? integration.descriptionEn : integration.description}
                      </p>

                      {integration.hasRateLimit && (
                        <div className="flex items-start gap-1.5 mt-2">
                          <AlertTriangle size={14} className="text-yellow-500 mt-0.5 flex-shrink-0" />
                          <p className="text-xs text-yellow-600">
                            {isEnglish ? integration.rateLimitNoteEn : integration.rateLimitNote}
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      {integration.enabled && (
                        <button
                          onClick={() => handleSync(integration.id)}
                          disabled={isSyncing}
                          className="p-2 rounded-lg bg-bg-surface hover:bg-bg-elevated text-text-secondary hover:text-text-primary transition-colors"
                          title={isEnglish ? 'Sync now' : 'Simdi senkronize et'}
                        >
                          <RefreshCw size={16} className={cn(isSyncing && 'animate-spin')} />
                        </button>
                      )}
                      <button
                        onClick={() => toggleIntegration(integration.id)}
                        className={cn(
                          'w-12 h-6 rounded-full transition-colors relative',
                          integration.enabled ? 'bg-[#FFD700]' : 'bg-bg-elevated border border-border-default'
                        )}
                      >
                        <div
                          className={cn(
                            'absolute top-1 w-4 h-4 rounded-full transition-all',
                            integration.enabled
                              ? 'right-1 bg-white'
                              : 'left-1 bg-text-tertiary'
                          )}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'sync' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-3">
                  {isEnglish ? 'Sync Frequency' : 'Senkronizasyon Sikligi'}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {syncFrequencyOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setSyncFrequency(option.value)}
                      className={cn(
                        'p-3 rounded-xl border text-sm font-medium transition-all',
                        syncFrequency === option.value
                          ? 'bg-[#FFD700]/10 border-[#FFD700] text-[#B8860B]'
                          : 'bg-bg-elevated border-border-default text-text-secondary hover:border-border-hover'
                      )}
                    >
                      {isEnglish ? option.labelEn : option.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-text-tertiary mt-2">
                  {isEnglish
                    ? 'Note: Actual sync frequency may be limited by PttAVM API rate limits.'
                    : 'Not: Gercek senkronizasyon sikligi PttAVM API rate limitleri tarafindan sinirlanabilir.'}
                </p>
              </div>
            </div>
          )}

          {activeTab === 'advanced' && (
            <div className="space-y-6">
              {/* Notifications */}
              <div className="flex items-center justify-between p-4 rounded-xl bg-bg-elevated border border-border-default">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#FFD700]/10 flex items-center justify-center">
                    <Bell size={20} className="text-[#B8860B]" />
                  </div>
                  <div>
                    <h4 className="font-medium text-text-primary">
                      {isEnglish ? 'Notifications' : 'Bildirimler'}
                    </h4>
                    <p className="text-sm text-text-secondary">
                      {isEnglish ? 'Get notified about sync status' : 'Senkronizasyon durumu hakkinda bilgilendirilir'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setNotifications(!notifications)}
                  className={cn(
                    'w-12 h-6 rounded-full transition-colors relative',
                    notifications ? 'bg-[#FFD700]' : 'bg-bg-surface border border-border-default'
                  )}
                >
                  <div
                    className={cn(
                      'absolute top-1 w-4 h-4 rounded-full transition-all',
                      notifications ? 'right-1 bg-white' : 'left-1 bg-text-tertiary'
                    )}
                  />
                </button>
              </div>

              {/* Info */}
              <div className="flex items-start gap-3 p-4 rounded-xl bg-blue-500/10 border border-blue-500/30">
                <Info size={20} className="text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="text-blue-400 font-medium mb-1">
                    {isEnglish ? 'PttAVM API Information' : 'PttAVM API Bilgisi'}
                  </p>
                  <p className="text-text-secondary">
                    {isEnglish
                      ? 'PttAVM uses SOAP-based web services with rate limits. Order date range is limited to 40 days.'
                      : 'PttAVM rate limitli SOAP tabanli web servisleri kullanir. Siparis tarih araligi 40 gun ile sinirlidir.'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t border-border-default bg-bg-elevated flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-bg-surface border border-border-default text-text-secondary hover:text-text-primary transition-colors"
          >
            {isEnglish ? 'Cancel' : 'Iptal'}
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className={cn(
              'px-4 py-2 rounded-xl font-medium transition-all flex items-center gap-2',
              'bg-[#FFD700] text-gray-800 hover:bg-[#FFA500]',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {isSaving ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                {isEnglish ? 'Saving...' : 'Kaydediliyor...'}
              </>
            ) : (
              <>
                <Check size={16} />
                {isEnglish ? 'Save' : 'Kaydet'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
